import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PresentationStatus } from '../store/registrationsSlice'
import {
  MAX_FILE_SIZE,
  ACCEPT_ATTR,
  isAllowedFile,
  formatBytes,
} from '../utils/presentationValidation'
import { Button } from './Button'
import { FileDropZone, Modal } from '@proaxiv/ui'

// ── Icons ──────────────────────────────────────────────────────────────────────

const UploadIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="text-pax-gray"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  /** Current presentation status — must not be `'none'` (caller is responsible for gating). */
  status: PresentationStatus
  fileName?: string
  uploadedAt?: string
  rejectionMessage?: string
  /** Whether a download/view is currently possible (e.g. a presigned URL or blob is available). */
  canDownload?: boolean
  onClose: () => void
  /** Uploads/resubmits a file. Resolves on success; the caller updates `status` afterwards. */
  onUpload: (file: File) => Promise<void>
  /** Opens/downloads the current file. Omit to hide the download affordance. */
  onDownload?: () => Promise<void>
}

/**
 * Participant-facing popup for managing their own presentation submission, opened from a button
 * on the ticket page (or automatically via the `?presentation=upload` URL query param).
 *
 * Mirrors the look of the organizer's backup-upload modal (`PresentationUploadModal`), but adapts
 * its content to the current lifecycle status:
 *  - `reminder_sent` / `rejected` → file picker + clear selection + "Submit for approval" / "Resubmit"
 *  - `uploaded` (pending review)  → file info + download only (no delete after submission)
 *  - `accepted`                   → locked, download only
 */
export function TicketPresentationModal({
  status,
  fileName,
  rejectionMessage,
  canDownload,
  onClose,
  onUpload,
  onDownload,
}: Props) {
  const { t } = useTranslation()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const canPickFile = status === 'reminder_sent' || status === 'rejected'
  const showDownload = canDownload !== false && !!onDownload
  const isPending = status === 'uploaded'
  const isLocked = status === 'accepted'

  function handleFileSelected(file: File) {
    setError(null)

    if (file.size > MAX_FILE_SIZE) {
      setError(t('pres_err_size'))
      setSelectedFile(null)
      return
    }
    if (!isAllowedFile(file)) {
      setError(t('pres_err_type'))
      setSelectedFile(null)
      return
    }
    setSelectedFile(file)
  }

  function handleClearSelection() {
    setSelectedFile(null)
    setError(null)
  }

  async function handleConfirmUpload() {
    if (!selectedFile || uploading) return
    setUploading(true)
    try {
      await onUpload(selectedFile)
      setSelectedFile(null)
      setError(null)
      onClose()
    } catch {
      setError(t('pres_err_upload'))
    } finally {
      setUploading(false)
    }
  }

  async function handleDownload() {
    if (!onDownload || downloading) return
    setDownloading(true)
    try {
      await onDownload()
    } catch {
      setError(t('pres_err_download'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Modal labelledBy="ticket-pres-modal-title">
      <div className="border-pax-purple/50 w-full max-w-md rounded-2xl border bg-white p-6 shadow-2xl ring-1 ring-black/5">
        <div className="mb-1 flex items-start justify-between gap-3">
          <h2 id="ticket-pres-modal-title" className="text-pax-black text-xl font-bold">
            {t('ticket_pres_modal_title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-pax-gray hover:text-pax-black shrink-0 cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Pending review — submitted; download only */}
        {isPending && (
          <div className="flex flex-col gap-4">
            <p className="text-pax-gray text-sm">{t('ticket_pres_modal_subtitle_pending')}</p>
            {fileName && (
              <p className="text-pax-black border-pax-purple/30 rounded-xl border bg-white p-3 text-sm font-medium">
                {fileName}
              </p>
            )}
            {error && (
              <p role="alert" className="text-pax-red text-sm font-medium">
                {error}
              </p>
            )}
            {showDownload && (
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  onClick={() => void handleDownload()}
                  disabled={downloading}
                >
                  {t('ticket_pres_download')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Locked / accepted */}
        {isLocked && (
          <div className="flex flex-col gap-4">
            <p className="text-pax-gray text-sm">{t('ticket_pres_locked_note')}</p>
            {fileName && (
              <p className="text-pax-black border-pax-purple/30 rounded-xl border bg-white p-3 text-sm font-medium">
                {fileName}
              </p>
            )}
            {error && (
              <p role="alert" className="text-pax-red text-sm font-medium">
                {error}
              </p>
            )}
            {showDownload && (
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  onClick={() => void handleDownload()}
                  disabled={downloading}
                >
                  {t('ticket_pres_download')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Picker — reminder_sent / rejected */}
        {canPickFile && (
          <div className="flex flex-col gap-1">
            <p className="text-pax-gray mb-3 text-sm">
              {status === 'rejected'
                ? t('ticket_pres_modal_subtitle_rejected')
                : t('ticket_pres_modal_subtitle_pick')}
            </p>

            {status === 'rejected' && rejectionMessage && (
              <div className="border-pax-red/20 mb-3 rounded-xl border bg-white p-3 text-sm">
                <p className="text-pax-gray mb-1 text-xs font-semibold tracking-wide uppercase">
                  {t('ticket_pres_rejected_label')}
                </p>
                <p className="text-pax-black">{rejectionMessage}</p>
              </div>
            )}

            {!selectedFile && (
              <FileDropZone
                accept={ACCEPT_ATTR}
                disabled={uploading}
                onFileSelected={handleFileSelected}
                icon={<UploadIcon />}
                uploadLabel={
                  <>
                    <span className="text-pax-black text-sm font-medium">
                      {t('pres_upload_choose')}
                    </span>
                    <span className="text-pax-gray text-xs">{t('pres_upload_constraints')}</span>
                  </>
                }
                className="mb-1"
                dropzoneClassName="border-pax-purple/40 hover:border-pax-blue hover:bg-pax-light-purple/30 flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors focus:outline-none focus-visible:ring-2"
                dropzoneActiveClassName="border-pax-blue bg-pax-light-purple/30"
              />
            )}

            {selectedFile && !error && (
              <div className="border-pax-purple/30 mb-4 flex items-center justify-between gap-3 rounded-xl border bg-white p-3">
                <p className="text-pax-black min-w-0 truncate text-sm font-medium">
                  {selectedFile.name}{' '}
                  <span className="text-pax-gray font-normal">
                    ({formatBytes(selectedFile.size)})
                  </span>
                </p>
                <Button
                  variant="danger"
                  onClick={handleClearSelection}
                  disabled={uploading}
                  className="shrink-0"
                >
                  {t('ticket_pres_clear_file')}
                </Button>
              </div>
            )}

            {error && (
              <p role="alert" className="text-pax-red mb-4 text-sm font-medium">
                {error}
              </p>
            )}

            {!selectedFile && !error && <div className="mb-4" />}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
              <Button variant="secondary" onClick={onClose}>
                {t('btn_cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleConfirmUpload()}
                disabled={!selectedFile || uploading}
              >
                {status === 'rejected' ? t('ticket_pres_resubmit') : t('ticket_pres_submit')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
